import {
  afterRender,
  Component,
  computed,
  effect,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  input,
  Input,
  output,
  PLATFORM_ID,
  Signal,
  signal,
  viewChild,
  WritableSignal
} from '@angular/core';
import {GridService} from '@core/services/grid.service';

import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {RefinementListComponent} from '../refinement-list/refinement-list.component';
import {RangeInputComponent} from '../range-input/range-input.component';
import {isPlatformBrowser, NgClass} from '@angular/common';
import {AlgoliaService} from '@core/services/algolia.service';
import {RatingFilterComponent} from '../rating-filter/rating-filter.component';
import {AlgoliaFacetFilter} from '@core/types/algolia-filter';
import {
  RangeRenderState as RatingRenderState,
  ReviewerFilter
} from '@core/instantsearch-connectors/connectRatingReviewerFilter';
import {InstantSearchComponent} from '@core/instantsearch/components/instantsearch.component';
import {
  InstantSearchBaseRangeInputComponent
} from '@core/instantsearch/components/instantsearch-base-range-input.component';
import {faAngleDown, faAngleUp} from '@fortawesome/free-solid-svg-icons';
import {RefinementListRenderState} from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';
import {RangeRenderState} from 'instantsearch.js/es/connectors/range/connectRange';
import {
  RangeSliderComponent
} from '@modules/page/components/search-page/components/range-slider/range-slider.component';
import {StoreConfigService} from '@core/services/store-config.service';

interface FilterSelected {
  nr: number;
  labels: string[];
}

@Component({
  selector: 'div[app-top-filter-facet]',
  templateUrl: './top-filter-facet.component.html',
  styleUrls: ['./top-filter-facet.component.scss'],
  imports: [NgClass, FontAwesomeModule, RefinementListComponent, RangeInputComponent, RatingFilterComponent, RangeSliderComponent]
})
export class TopFilterFacetComponent {
  gridService = inject(GridService);
  private elRef = inject(ElementRef);
  private algoliaService = inject(AlgoliaService);
  private platformId = inject(PLATFORM_ID);
  private instantSearchInstance = inject(InstantSearchComponent);
  private storeConfigService = inject(StoreConfigService)

  sortBy = computed(() => this.algoliaService.getSortByForFilter(this.attribute()));

  public ratingsMin: number = 80;
  public ratingsMax: number = 100;
  public _isSelected = signal(false);
  public currencyCode = this.storeConfigService.currencyCode;

  public selected: WritableSignal<FilterSelected> = signal({nr: 0, labels: []});
  public offsetTop = signal(0);
  public refinementList = viewChild<RefinementListComponent>('refinementList');
  public rangeInput = viewChild<InstantSearchBaseRangeInputComponent>('rangeInput');
  public ratingFilterInput = viewChild<RatingFilterComponent>('ratingFilterInput');
  public showFilterSubTitle = computed(() => isPlatformBrowser(this.platformId) && !this.isDesktop());
  readonly hasLoadedHits = input(false);
  public shouldFacetBeVisible: Signal<boolean> = computed(() => {
    if (!this.hasLoadedHits()) {
      return true;
    }
    return this.getShouldFacetBeVisible(this.selected(), this.ratingFilterInput()?.state(), this.refinementList()?.state())
  });
  readonly hideExtraFilters = input.required<boolean>();

  readonly selectChange = output();
  readonly hideFilter = output<boolean>();
  public isHidden = computed(() => {
    return this.hideExtraFilters() && this.offsetTop() > 20 && this.isDesktop();
  })
  @HostBinding('class.display-none') displayNone: boolean = false;
  readonly attribute = input.required<AlgoliaFacetFilter>();

  @Input()
  set isSelected(value: boolean) {
    this._isSelected.set(value);
  }
  private lastRange: { min?: number, max?: number } = {min: 0, max: 0};
  @HostBinding('class.hidden') hidden: boolean = false;

  constructor() {
    effect(() => {
      const rangeInput = this.rangeInput();
      if (this.isRangeInput() && rangeInput) {
        this.checkRangeInput(rangeInput.state());
      }
      const refinementListState = this.refinementList()?.state();
      if (this.isRefinementList() && refinementListState) {
        this.checkRefinementList(refinementListState)
      }
    });

    effect(() => {
      const hide = this.isHidden();
      if (this.hidden !== hide) {
        this.hidden = hide;
        this.hideFilter.emit(hide);
      }
    });

    effect(() => {
      this.displayNone = !this.shouldFacetBeVisible();
    });

    afterRender({
      read: () => {
        this.offsetTop.set(this.elRef.nativeElement.offsetTop);
      }
    })
  }

  get isDesktop() {
    return this.gridService.isMediumSignal;
  }

  @HostListener('mouseleave', ['$event'])
  mouseleave(event: any) {
    this.mouseAction(false);
  }

  updateSelectedRange(event: { min?: number, max?: number }) {
    if (!this.isRangeInput() && !this.isRangeSlider()) {
      return;
    }
    const valid = this.lastRange.min !== event.min || this.lastRange.max !== event.max;
    if (!valid) {
      return;
    }
    let number = 0;
    const labels: any = [];

    if (event.min) {
      labels.push(`≥${event.min}`);
      number += 1;
    }

    if (event.max) {
      labels.push(`≤${event.max}`);
      number += 1;
    }
    this.selected.set({
      labels,
      nr: number
    });
    this.lastRange = event;
  }

  checkRangeInput(rangeInputState: RangeRenderState) {
    if (rangeInputState?.start) {
      const min: any = rangeInputState.start[0] !== -Infinity ? rangeInputState.start[0] : 0;
      const max: any = rangeInputState.start[1] !== Infinity ? rangeInputState.start[1] : 0;
      this.updateSelectedRange({min, max});
    }
  }

  checkRatingFilter(ratingFilterState: RatingRenderState|undefined, selected: FilterSelected) {
    return ratingFilterState?.canRefine || selected['nr'] !== 0;
  }

  refinementListShouldBeVisible(refinementListState: RefinementListRenderState | undefined) {
    const refinementListItemCount = refinementListState?.items?.length || 0;
    if (!refinementListState || refinementListItemCount <= 0) {
      return false;
    }

    const key = Object.keys(this.instantSearchInstance.instantSearchInstance.renderState)[0];
    const totalHits = this.instantSearchInstance.instantSearchInstance.renderState[key].stats?.nbHits || 0
    const isRefined = refinementListState.items[0].isRefined;
    const filterOptionCount = refinementListState.items[0].count as any as string;
    const filterOptionHits = parseInt(filterOptionCount.replace('(', '').replace(')', ''), 10)
    if (refinementListItemCount === 1 && !isRefined && filterOptionHits === totalHits) {
      return false;
    }
    return true;
  }

  checkRefinementList(refinementListState: RefinementListRenderState) {
    if (!this.refinementListShouldBeVisible(refinementListState)) {
      return;
    }

    let nr = 0;
    const labels: string[] = [];

    refinementListState.items?.forEach(item => {
      if (item.isRefined) {
        nr += 1;
        labels.push(this.replaceText(item.label));
      }
    });
    this.selected.set({nr, labels})
  }

  getShouldFacetBeVisible(selected: FilterSelected, ratingState: RatingRenderState|undefined, refinementListState: RefinementListRenderState | undefined) {
    if (this.isRangeInput()) {
      return true;
    }
    if (this.isRangeSlider()) {
      return true;
    }
    if (this.isRatingFilter()) {
      return this.checkRatingFilter(ratingState, selected);
    }
    if (this.isRefinementList()) {
      return this.refinementListShouldBeVisible(refinementListState);
    }
    return false;
  }

  replaceText(item: string) {
    return this.algoliaService.getLabel(item);
  }

  transformItems = (items: any[]) => {
    return items.map(item => ({
      ...item,
      count: `(${item.count})`,
      highlighted: this.replaceText(item.label) ?? item.highlighted
    }));
  }

  isRangeSlider() {
    return this.attribute().instantSearchType === 'range-slider';
  }

  isRangeInput() {
    return this.attribute().instantSearchType === 'range-input';
  }

  isRatingFilter() {
    return this.attribute().instantSearchType === 'ratings-filter';
  }

  isRefinementList() {
    return this.attribute().instantSearchType === 'refinement-list';
  }

  @HostListener('mouseenter', ['$event'])
  mouseenter(event: any) {
    this.mouseAction(true);
  }

  setHiddenState() {
    if (this.isDesktop()) {
      return;
    }
    this.selectChange.emit();
  }

  mouseAction(value: boolean) {
    if (this.isDesktop()) {
      this._isSelected.set(value);

      if (!this.refinementList) {
        return;
      }
    }
  }

  protected getSelectedByRatingChangeEvent(event: ReviewerFilter) {
    const labels: string[] = [];
    let number = 0;

    if (event.reviewers.length === 0 && event.range.min === this.ratingsMin && event.range.max === this.ratingsMax) {
      return {labels, nr: number};
    }

    if (event.range.min !== this.ratingsMin) {
      labels.push(`≥${event.range.min}`);
      number += 1;
    }
    if (event.range.max !== this.ratingsMax) {
      labels.push(`≤${event.range.max}`);
      number += 1;
    }

    if (event.reviewers.length === 0) {
      labels.push($localize`Alle anmeldere`);
    } else if (event.reviewers.length === 1) {
      labels.push($localize`${event.reviewers.length} Anmelder`);
      number += event.reviewers.length;
    } else {
      labels.push($localize`${event.reviewers.length} Anmeldere`);
      number += event.reviewers.length;
    }
    return {labels, nr: number};
  }

  onRatingFilterChange(event: ReviewerFilter) {
    this.selected.set(this.getSelectedByRatingChangeEvent(event));
  }

  protected readonly faAngleDown = faAngleDown;
  protected readonly faAngleUp = faAngleUp;
}
